import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { Col, Image, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import {
  DragDropContext,
  Droppable,
  Draggable
} from '@hello-pangea/dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripVertical } from '@fortawesome/free-solid-svg-icons'

import IconDelete from 'assets/icons/delete.svg'
import IconBullet from 'assets/icons/bullet-point.svg'

import Card from 'components/Card'
import TouchableOpacity from 'components/TouchableOpacity'
import FormControl from 'components/Form/FormControl'
import Button from 'components/Button'

import memberStyles from 'pages/Member/styles.module.scss'

import styles from '../styles.module.scss'
import { goalSchema } from '../constants'

const Add = ({ isLoading, submit, toggleAddMode }) => {
  const [actionSteps, setActionSteps] = useState([
    {
      id: 1,
      description: ''
    }
  ])
  const [actionStepError, setActionStepError] = useState(false)
  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(goalSchema)
  })

  const addActionStep = () => {
    setActionStepError(null)
    const lastStepId = actionSteps.length
      ? actionSteps[actionSteps.length - 1].id
      : 0
    setActionSteps([
      ...actionSteps,
      { id: lastStepId + 1, description: '' }
    ])
  }

  const removeActionStep = id => {
    const hasOneActionStep = actionSteps?.length === 1

    if (!hasOneActionStep) {
      const output = actionSteps.filter(step => step.id !== id)
      setActionSteps(output)
    } else {
      setActionStepError('At least one action step is required')
    }
  }

  const actionStepOnChange = (id, newValue) => {
    const index = actionSteps.findIndex(step => step.id === id)
    actionSteps[index].description = newValue
  }

  const onDragEnd = result => {
    if (!result.destination) return
    const reordered = [...actionSteps]
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)
    setActionSteps(reordered)
  }

  const addGoalSubmit = ({ title }) => {
    const validSteps = actionSteps.filter(
      step => !isEmpty(step.description)
    )
    const isValid = actionSteps.length === validSteps.length

    if (isValid) {
      setActionStepError(null)
      submit({
        title,
        actionSteps
      })
    } else {
      setActionStepError(
        'All action steps require a description'
      )
    }
  }
  return (
    <Card className={`${styles.devPlanCard} mb-20`}>
      <Row>
        <Col md={12}>
          <FormControl
            placeholder="Goal"
            formGroupClass="mb-8"
            as="textarea"
            rows={1}
            autoGrow
            name="title"
            inputRef={register}
            error={errors.title}
          />
        </Col>
      </Row>
      <div className="mb-20">
        <Col xs={12} className={memberStyles.dividerLine} />
      </div>
      <div className="d-flex justify-content-between mb-10">
        <div className="font-weight-medium text-primary">
          Action items:
        </div>
        <TouchableOpacity
          className="text-secondary"
          onClick={addActionStep}
        >
          + Add action item
        </TouchableOpacity>
      </div>
      <div className="mb-30">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="member-add-action-steps">
            {droppableProvided => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {actionSteps.map((step, index) => (
                  <Draggable
                    key={`add-plan-action-${step.id}`}
                    draggableId={`add-plan-action-${step.id}`}
                    index={index}
                  >
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`d-flex mb-10 ${
                          styles.actionStepRow
                        } ${
                          snapshot.isDragging
                            ? styles.actionStepDragging
                            : ''
                        }`}
                      >
                        <div
                          className={styles.dragHandle}
                          {...dragProvided.dragHandleProps}
                        >
                          <FontAwesomeIcon
                            icon={faGripVertical}
                          />
                        </div>
                        <Image
                          src={IconBullet}
                          className={`mr-10 ${styles.actionStepBullet}`}
                        />
                        <FormControl
                          placeholder="Action step description"
                          as="textarea"
                          rows={1}
                          autoGrow
                          formGroupClass={`${styles.actionItemInput} mb-0 mr-20`}
                          defaultValue={step.description}
                          onChange={e => {
                            actionStepOnChange(
                              step.id,
                              e.target.value
                            )
                          }}
                        />
                        <TouchableOpacity
                          className={`d-flex align-items-center ${styles.actionStepDelete}`}
                          onClick={() =>
                            removeActionStep(step.id)
                          }
                        >
                          <Image src={IconDelete} />
                        </TouchableOpacity>
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {actionStepError && (
          <small className="text-danger">
            {actionStepError}
          </small>
        )}
      </div>
      <div className="d-flex justify-content-end mb-20">
        <Button
          className="d-none d-sm-block"
          variant="link"
          smallPadding
          onClick={toggleAddMode}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className="form__button--submit"
          variant="secondary"
          smallPadding
          onClick={handleSubmit(addGoalSubmit)}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Save'}
        </Button>
      </div>
    </Card>
  )
}

Add.propTypes = {
  isLoading: PropTypes.bool,
  submit: PropTypes.func,
  toggleAddMode: PropTypes.func
}

export default Add
